import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { listCategories, Category } from "../../../services/productApiService";
import placeholderImage from "../../../assets/placeholders/picture.webp";
import { CustomCard, CustomCardTitle } from "@repo/ui";
import withAnimation from "../../../hoc/withCardAnimation/withCardAnimation";
import arrowIcon from "../../../assets/icons/arrow_forward.svg";
import { useMediaQuery } from "react-responsive";
import { withTranslations } from "../../../hoc/withTranslations/withTranslations";

interface CategoriesMenuProps {
  onCategorySelect: (category: Category | null) => void;
}

const AnimatedCard = withAnimation(CustomCard);

const LOCALE = "modules_MyGiftTable_components_CategoriesMenu";
const CategoriesMenu: React.FC<CategoriesMenuProps> = ({
  onCategorySelect,
}) => {
  const { t } = useTranslation(LOCALE);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await listCategories();
        setCategories(response);
      } catch (error) {
        console.error(t("errorFetchingCategories"), error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  if (loading) {
    return <p>{t("loading")}</p>;
  }

  return (
    <div className="p-4">
      {/* Introductory text */}
      <p className="text-lg mb-4 text-center text-primary mb-8">
        {t("introText")}
      </p>

      {/* Categories Grid */}
      <div
        className="grid gap-4 md:gap-8"
        style={{
          gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "150" : "200"}px, 1fr))`,
        }}
      >
        {categories.length === 0 ? (
          <p>{t("noCategories")}</p>
        ) : (
          <>
            {categories.map((category) => (
              <div
                key={category.id}
                onClick={() => onCategorySelect(category)}
                className="relative group cursor-pointer"
              >
                <AnimatedCard className="relative rounded-s shadow-none border transition-transform transform group-hover:scale-102 group-hover:shadow-lg">
                  <div className="flex items-center justify-center h-64 overflow-hidden">
                    <img
                      src={category.front_page || placeholderImage}
                      alt={category.name}
                      className="object-cover w-full h-full"
                    />
                  </div>
                  <div className="p-4 text-center">
                    <CustomCardTitle>{category.name}</CustomCardTitle>
                    <p className="text-gray-600">{category.description}</p>
                  </div>
                </AnimatedCard>
              </div>
            ))}

            {/* "All Categories" Card */}
            <div
              onClick={() => onCategorySelect(null)}
              className="relative group cursor-pointer"
            >
              <AnimatedCard className="relative rounded-s shadow-none border transition-transform transform group-hover:scale-102 group-hover:shadow-lg">
                <div className="flex items-center justify-center h-64 overflow-hidden bg-gray-100">
                  <img
                    src={arrowIcon}
                    alt={t("allCategories")}
                    className="w-12 h-12"
                  />
                </div>
                <div className="p-4 text-center">
                  <CustomCardTitle>{t("allCategories")}</CustomCardTitle>
                </div>
              </AnimatedCard>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default withTranslations(LOCALE)(CategoriesMenu);
